import React, { useEffect, useMemo, useState } from 'react';
import { coreModules } from '../../../executive/coreModules/coreModulesConfig';
import { referenceApi } from '../../../../api/reference';
import { marketsApi } from '../../../../api/marketsApi';
import { borderCrossingsApi } from '../../../../api/borderCrossingsApi';
import { vetCertificatesApi } from '../../../../api/vetCertificatesApi';
import { livestockActorsApi } from '../../../../api/workforceApi';
import { workplanActivitiesApi } from '../../../../api/planningApi';
import { pppArrangementsApi } from '../../../../api/pppArrangementsApi';
import { useLocationOptions } from '../../shared/useLocationOptions';
import GpsCoordinateField from '../../../shared/GpsCoordinateField';
import FileUploadField from '../../../shared/FileUploadField';
import { Field, Select, SelectWithOther, INP } from '../../../reusable/formRecords/formFieldPrimitives';
import { createFieldBlueprint, FIELD_SELECTOR_TEMPLATES, REGISTRY_SUBMODULE_OPTIONS } from './formRegistryData';

export const MODULE_SUBMODULE_MAP = REGISTRY_SUBMODULE_OPTIONS;

export const FIELD_SOURCE_MAP = {
  module: 'coreModules',
  'livestock-species': 'reference:species',
  species: 'reference:species',
  'production-system': 'reference:productionSystems',
  'production-purpose': 'reference:productionPurposes',
  'count-method': 'reference:countMethods',
  'data-source': 'reference:dataSources',
  'age-group': 'reference:ageClasses',
  'breed-ecotype': 'reference:breeds',
  'issuing-institution': 'reference:institutions',
  'origin-market': 'reference:markets',
  market: 'reference:markets',
  'species-covered': 'reference:species',
  'linked-border-crossing': 'reference:borderCrossings',
  'vet-certificate-ref': 'reference:vetCertificates',
};

const SOURCE_CACHE = new Map();

function normalizeOptions(options) {
  return (Array.isArray(options) ? options : [])
    .map((option) => {
      if (option === null || option === undefined) return null;

      if (typeof option === 'string' || typeof option === 'number') {
        const value = String(option).trim();
        return value ? { value, label: value } : null;
      }

      if (typeof option !== 'object') return null;

      const value = String(
        option.value ??
        option.uuid ??
        option.id ??
        option.code ??
        option.slug ??
        option.market_uuid ??
        option.market_id ??
        option.species_uuid ??
        option.breed_uuid ??
        option.certificate_number ??
        option.border_point_id ??
        option.name ??
        option.label ??
        option.title ??
        option.market_name ??
        option.marketName ??
        option.common_name_nigeria ??
        option.commonNameNigeria ??
        option.breed_name ??
        option.breedName ??
        option.age_class_label ??
        option.ageClassLabel ??
        option.age_class_code ??
        option.fullName ??
        option.full_name ??
        option.pppArrangementId ??
        option.ppp_arrangement_id ??
        option.partnerOrgName ??
        ''
      ).trim();

      if (!value) return null;

      const label = String(
        option.label ??
        option.title ??
        option.name ??
        option.market_name ??
        option.marketName ??
        option.common_name_nigeria ??
        option.commonNameNigeria ??
        option.breed_name ??
        option.breedName ??
        option.age_class_label ??
        option.ageClassLabel ??
        option.certificate_number ??
        option.fullName ??
        option.full_name ??
        option.pppArrangementId ??
        option.ppp_arrangement_id ??
        option.partnerOrgName ??
        value
      ).trim() || value;

      return { value, label, raw: option };
    })
    .filter(Boolean);
}

function getSourceKey(field) {
  return field?.source ?? FIELD_SOURCE_MAP[field?.id] ?? null;
}

async function loadSourceOptions(sourceKey, field) {
  switch (sourceKey) {
    case 'coreModules':
      return coreModules.map((module) => ({ value: module.id, label: module.name }));
    case 'livestockSubModules':
      return REGISTRY_SUBMODULE_OPTIONS['livestock-population'] ?? [];
    case 'marketSubModules':
      return REGISTRY_SUBMODULE_OPTIONS['markets-trade'] ?? [];
    case 'reference:species':
      return referenceApi.species(field?.params);
    case 'reference:breeds':
      return referenceApi.breeds(field?.params);
    case 'reference:productionSystems':
      return referenceApi.productionSystems(field?.params);
    case 'reference:productionPurposes':
      return referenceApi.productionPurposes(field?.params);
    case 'reference:countMethods':
      return referenceApi.countMethods(field?.params);
    case 'reference:dataSources':
      return referenceApi.dataSources(field?.params);
    case 'reference:ageClasses':
      return referenceApi.ageClasses(field?.params);
    case 'reference:institutions':
      return referenceApi.institutions(field?.params);
    case 'reference:markets':
      return (await marketsApi.listAll({ per_page: 500, ...(field?.params ?? {}) })).items;
    case 'reference:borderCrossings':
      return (await borderCrossingsApi.listAll({ per_page: 500, ...(field?.params ?? {}) })).items;
    case 'reference:vetCertificates':
      return (await vetCertificatesApi.listAll({ per_page: 500, ...(field?.params ?? {}) })).items;
    case 'reference:livestockActors':
      return (await livestockActorsApi.listAll({ per_page: 500, ...(field?.params ?? {}) })).items;
    case 'reference:workplanActivities':
      return (await workplanActivitiesApi.listAll({ per_page: 500, ...(field?.params ?? {}) })).items;
    case 'reference:pppArrangements':
      return (await pppArrangementsApi.listAll({ per_page: 500, ...(field?.params ?? {}) })).items;
    default:
      return [];
  }
}

function useFieldOptions(field) {
  const sourceKey = getSourceKey(field);
  const paramsKey = useMemo(() => JSON.stringify(field?.params ?? {}), [field?.params]);
  const fallbackOptions = useMemo(() => normalizeOptions(field?.options), [field?.options]);
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [loading, setLoading] = useState(Boolean(sourceKey));

  useEffect(() => {
    let active = true;

    if (!sourceKey) {
      setRemoteOptions([]);
      setLoading(false);
      return undefined;
    }

    const cacheKey = `${sourceKey}:${paramsKey}`;
    const cached = SOURCE_CACHE.get(cacheKey);
    if (cached) {
      setRemoteOptions(cached);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    loadSourceOptions(sourceKey, field)
      .then((items) => {
        if (!active) return;
        const normalized = normalizeOptions(items);
        SOURCE_CACHE.set(cacheKey, normalized);
        setRemoteOptions(normalized);
      })
      .catch(() => {
        if (!active) return;
        setRemoteOptions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [field, paramsKey, sourceKey]);

  const options = remoteOptions.length > 0 ? remoteOptions : fallbackOptions;

  return { options, loading };
}

export function DropdownComponent({ field, disabled = false, value, onChange, options: overrideOptions, loading: overrideLoading }) {
  const { options: hookOptions, loading: hookLoading } = useFieldOptions(field);
  const options = overrideOptions ?? hookOptions;
  const loading = overrideLoading ?? hookLoading;
  const [internalValue, setInternalValue] = useState(field.value ?? value ?? '');

  useEffect(() => {
    setInternalValue(field.value ?? value ?? '');
  }, [field.value, value]);

  const currentValue = value ?? field.value ?? internalValue;

  const handleChange = (event) => {
    const nextValue = event.target.value;
    setInternalValue(nextValue);
    if (onChange) {
      const selected = options.find((option) => option.value === nextValue);
      onChange(nextValue, selected?.label ?? nextValue);
    }
  };

  return (
    <Field label={field.label} required={field.required} hint={field.hint}>
      <Select
        value={currentValue}
        onChange={handleChange}
        disabled={disabled}
        options={options}
        placeholder={loading ? 'Loading...' : (field.placeholder || 'Select...')}
      />
    </Field>
  );
}

export function InputComponent({ field, disabled = false, value, onChange }) {
  const [internalValue, setInternalValue] = useState(field.value ?? value ?? '');
  const inputType = field.type === 'number' ? 'number' : 'text';

  useEffect(() => {
    setInternalValue(field.value ?? value ?? '');
  }, [field.value, value]);

  const currentValue = value ?? field.value ?? internalValue;

  return (
    <Field label={field.label} required={field.required} hint={field.hint}>
      <input
        value={currentValue}
        onChange={(event) => {
          setInternalValue(event.target.value);
          onChange?.(event.target.value);
        }}
        type={inputType}
        inputMode={field.type === 'number' ? 'numeric' : undefined}
        placeholder={field.placeholder || (field.type === 'number' ? '0' : 'Enter text')}
        disabled={disabled}
        className={INP}
      />
    </Field>
  );
}

export function RadioComponent({ field, disabled = false, value, onChange }) {
  const options = useMemo(() => normalizeOptions(field.options).slice(0, 4), [field.options]);
  const [internalValue, setInternalValue] = useState(field.value ?? value ?? '');

  useEffect(() => {
    setInternalValue(field.value ?? value ?? '');
  }, [field.value, value]);

  const currentValue = value ?? field.value ?? internalValue;

  return (
    <Field label={field.label} required={field.required} hint={field.hint}>
      <div className="flex flex-wrap gap-5 pt-1">
        {options.map((option) => (
          <label key={option.value} className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700">
            <input
              type="radio"
              name={field.id}
              value={option.value}
              checked={currentValue === option.value}
              onChange={(event) => {
                setInternalValue(event.target.value);
                onChange?.(event.target.value, option.label);
              }}
              disabled={disabled}
              className="accent-green-600"
            />
            {option.label}
          </label>
        ))}
      </div>
    </Field>
  );
}

// Tick one or more values; the value is the array of ticked option values.
// Ticking one of `field.exclusiveOptions` clears the others (and ticking any
// other option clears it) — e.g. Sex: Male / Female, or Combined.
export function CheckboxComponent({ field, disabled = false, value, onChange }) {
  const options = useMemo(() => normalizeOptions(field.options), [field.options]);
  const exclusive = useMemo(
    () => new Set((field.exclusiveOptions ?? []).map((option) => String(option).toLowerCase())),
    [field.exclusiveOptions]
  );
  const toList = (raw) => (Array.isArray(raw) ? raw : (raw ? [raw] : []));
  const [internalValue, setInternalValue] = useState(toList(field.value ?? value));

  useEffect(() => {
    setInternalValue(toList(field.value ?? value));
  }, [field.value, value]);

  const current = value !== undefined ? toList(value) : internalValue;
  const isExclusive = (option) => exclusive.has(String(option.value).toLowerCase()) || exclusive.has(String(option.label).toLowerCase());

  const toggle = (option, checked) => {
    let next;
    if (!checked) next = current.filter((item) => item !== option.value);
    else if (isExclusive(option)) next = [option.value];
    else next = [...current.filter((item) => !options.some((o) => o.value === item && isExclusive(o))), option.value];
    setInternalValue(next);
    onChange?.(next);
  };

  return (
    <Field label={field.label} required={field.required} hint={field.hint}>
      <div className="flex flex-wrap gap-5 pt-1">
        {options.map((option) => (
          <label key={option.value} className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox"
              name={field.id}
              value={option.value}
              checked={current.includes(option.value)}
              onChange={(event) => toggle(option, event.target.checked)}
              disabled={disabled}
              className="h-4 w-4 rounded accent-green-600"
            />
            {option.label}
          </label>
        ))}
      </div>
    </Field>
  );
}

export function GeoLocationComponent({ field, disabled = false, value, onChange }) {
  const current = value ?? field.value ?? {};
  const [state, setState] = useState(current.state ?? '');
  const [lga, setLga] = useState(current.lga ?? '');
  const [ward, setWard] = useState(current.ward ?? '');
  const { stateOptions, lgaOptions, wardOptions, loadingStates, loadingLgas, loadingWards } = useLocationOptions(state, lga);

  useEffect(() => {
    const nextState = current.state ?? '';
    const nextLga = current.lga ?? '';
    const nextWard = current.ward ?? '';
    setState(nextState);
    setLga(nextLga);
    setWard(nextWard);
  }, [current.state, current.lga, current.ward]);

  const emitChange = (nextState, nextLga, nextWard) => {
    onChange?.({
      state: nextState,
      lga: nextLga,
      ward: nextWard,
    });
  };

  if (field.selectorPreview) {
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-bold text-gray-900">Geo Location</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="State" required={field.required}>
            <Select value="" disabled options={[]} placeholder="Select state" />
          </Field>
          <Field label="LGA" required={field.required}>
            <Select value="" disabled options={[]} placeholder="Select LGA" />
          </Field>
          <Field label="Ward">
            <Select value="" disabled options={[]} placeholder="Select ward" />
          </Field>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h4 className="mb-3 text-sm font-bold text-gray-900">Geo Location</h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Field label="State" required={field.required}>
        <Select
          value={state}
          onChange={(event) => {
            const nextState = event.target.value;
            setState(nextState);
            setLga('');
            setWard('');
            emitChange(nextState, '', '');
          }}
          disabled={disabled}
          options={stateOptions}
          placeholder={loadingStates ? 'Loading...' : 'Select state'}
        />
      </Field>
      <Field label="LGA" required={field.required}>
        <Select
          value={lga}
          onChange={(event) => {
            const nextLga = event.target.value;
            setLga(nextLga);
            setWard('');
            emitChange(state, nextLga, '');
          }}
          disabled={disabled || !state || loadingLgas}
          options={lgaOptions}
          placeholder={!state ? 'Select state first' : loadingLgas ? 'Loading...' : 'Select LGA'}
        />
      </Field>
      <Field label="Ward">
        <Select
          value={ward}
          onChange={(event) => {
            const nextWard = event.target.value;
            setWard(nextWard);
            emitChange(state, lga, nextWard);
          }}
          disabled={disabled || !lga || loadingWards}
          options={wardOptions}
          placeholder={!lga ? 'Select LGA first' : loadingWards ? 'Loading...' : 'Select ward'}
        />
      </Field>
        </div>
      </div>
    </div>
  );
}

export function FieldRenderer({ field, value, otherValue, disabled = false, onRemove, onEdit, onFieldChange, onOtherChange }) {
  const wrapperClass = field.layout === 'full-width' ? 'col-span-2' : 'col-span-1';
  const { options, loading } = useFieldOptions(field);

  return (
    <div className={`relative ${wrapperClass}`}>
      {(onEdit || onRemove) && (
        <div className="absolute -right-2 -top-2 z-10 flex gap-1">
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-gray-400 shadow ring-1 ring-gray-200 hover:text-green-600 hover:ring-green-200"
              title="Edit field"
              aria-label="Edit field"
            >
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
              </svg>
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-gray-400 shadow ring-1 ring-gray-200 hover:text-red-600 hover:ring-red-200"
              title="Remove field"
              aria-label="Remove field"
            >
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {field.type === 'dropdown' && field.allowOther ? (
        <SelectWithOther
          label={field.label}
          required={field.required}
          hint={field.hint}
          value={value ?? field.value ?? ''}
          otherValue={otherValue ?? field.otherValue ?? ''}
          onChange={(nextValue) => onFieldChange?.(field.id, nextValue)}
          onOtherChange={(nextOtherValue) => onOtherChange?.(field.otherFieldKey ?? `${field.id}_other`, nextOtherValue)}
          disabled={disabled}
          options={options}
          placeholder={loading ? 'Loading...' : (field.placeholder || 'Select...')}
          otherPlaceholder={field.otherPlaceholder || 'Please specify'}
          className={INP}
        />
      ) : field.type === 'dropdown' ? (
        <DropdownComponent
          field={field}
          disabled={disabled}
          value={value}
          options={options}
          loading={loading}
          onChange={onFieldChange ? (nextValue) => onFieldChange(field.id, nextValue) : undefined}
        />
      ) : field.type === 'text' || field.type === 'number' ? (
        <InputComponent
          field={field}
          disabled={disabled}
          value={value}
          onChange={(nextValue) => onFieldChange?.(field.id, nextValue)}
        />
      ) : field.type === 'checkbox' ? (
        <CheckboxComponent
          field={field}
          disabled={disabled}
          value={value}
          onChange={(nextValue) => onFieldChange?.(field.id, nextValue)}
        />
      ) : field.type === 'radio' ? (
        <RadioComponent
          field={field}
          disabled={disabled}
          value={value}
          onChange={(nextValue) => onFieldChange?.(field.id, nextValue)}
        />
      ) : field.type === 'geolocation' ? (
        <GeoLocationComponent
          field={field}
          disabled={disabled}
          value={value}
          onChange={(nextValue) => onFieldChange?.(field.id, nextValue)}
        />
      ) : field.type === 'gps' ? (
        <GpsCoordinateField
          label={field.label || 'GPS Coordinates'}
          required={field.required}
          value={value}
          onChange={(nextValue) => onFieldChange?.(field.id, nextValue)}
          disabled={disabled}
        />
      ) : field.type === 'attachment' ? (
        <FileUploadField
          label={field.label || 'Attached'}
          required={field.required}
          hint={field.hint}
          value={(value ?? field.value)?.value ?? ''}
          fileName={(value ?? field.value)?.fileName ?? ''}
          disabled={disabled}
          onChange={(dataUri, fileName) => onFieldChange?.(field.id, { value: dataUri, fileName })}
        />
      ) : null}
    </div>
  );
}

function FieldTypeRow({ template, onAddField, onSelectField }) {
  const [required, setRequired] = useState(false);

  const handleSelect = () => {
    const field = { ...createFieldBlueprint(template.type), required };
    if (onSelectField) {
      onSelectField(field);
      return;
    }
    onAddField?.(field);
  };

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-gray-800">{template.label}</span>
      </div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <label className="flex w-fit cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-500">
          <input
            type="checkbox"
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
            className="accent-green-600"
          />
          Required
          {required && <span className="text-red-500">*</span>}
        </label>
        <button
          type="button"
          onClick={handleSelect}
          className="inline-flex shrink-0 items-center rounded bg-green-500 px-3 py-1 text-xs font-semibold text-white hover:bg-green-600"
        >
          + Add
        </button>
      </div>
      <div className="pointer-events-none opacity-90">
        <FieldRenderer field={{ ...createFieldBlueprint(template.type), required, selectorPreview: true }} disabled />
      </div>
    </div>
  );
}

export function FormFieldSelectorModal({ open, onClose, onBack, onAddField, onSelectField }) {
  return (
    <>
      <div
        className={`fixed inset-0 z-[80] bg-slate-950/40 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />
      <aside className={`fixed right-0 top-0 z-[90] flex h-full w-full max-w-[420px] flex-col bg-white shadow-2xl transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <header className="flex items-center justify-between gap-3 rounded-t-lg bg-green-500 px-5 py-4">
          <h3 className="text-base font-bold text-white">Form Field Selector</h3>
          <button type="button" onClick={onClose} className="text-white/90 hover:text-white" aria-label="Close selector">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-auto px-5 py-5">
          {FIELD_SELECTOR_TEMPLATES.map((template) => (
            <FieldTypeRow key={template.type} template={template} onAddField={onAddField} onSelectField={onSelectField} />
          ))}
        </div>

        <footer className="border-t border-gray-100 px-5 py-4">
          {onBack ? (
            <div>
              <button type="button" onClick={onBack} className="h-11 w-full rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Back
              </button>
            </div>
          ) : (
            <button type="button" onClick={onClose} className="h-11 w-full rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
          )}
        </footer>
      </aside>
    </>
  );
}
